/**
 * Types for the TaskComposer scope menu. Scope constrains repos surfaced by the
 * composer (personal = user + public; organization = org + public). Billing is
 * unaffected.
 */

/** An organization the current user can scope the TaskComposer to. */
export interface AvailableOrganization {
  id: string
  login: string
  name: string
  avatarUrl: string
  /** True when the user still needs to complete SAML/SSO for this org. */
  requiresSso: boolean
}

/** The selected TaskComposer scope. */
export type TaskComposerScope = {kind: 'personal'} | {kind: 'organization'; organization: AvailableOrganization}

/** The default scope when no persisted selection exists. */
export const DEFAULT_TASK_COMPOSER_SCOPE: TaskComposerScope = {kind: 'personal'}

/** Convenience: extract the org login from a scope, or `undefined` for personal. */
export function scopeOrgLogin(scope: TaskComposerScope): string | undefined {
  return scope.kind === 'organization' ? scope.organization.login : undefined
}

/**
 * Returns true when a repo remains valid under the active scope.
 *
 * A repo is in scope when:
 *   - no org scope is active (personal scope surfaces all accessible repos), or
 *   - the repo is public (public repos appear in every scope), or
 *   - the repo owner matches the scope owner (case-insensitive).
 */
export function isRepoInScope(
  repo: {owner?: string; private?: boolean} | null | undefined,
  scopeOwner: string | undefined,
): boolean {
  if (!repo) return true
  if (!scopeOwner) return true
  if (repo.private === false) return true
  return (repo.owner ?? '').toLowerCase() === scopeOwner.toLowerCase()
}
